import { subMonths, subYears } from "date-fns";
import { BodyMetricType } from "../../../enums";
import {
  BodyMetricRange,
  IBodyMetricEntry,
  IBodyMetricSummary,
} from "../models/bodyMetricModels";
import { BODY_METRIC_TYPES, BODY_METRIC_TYPE_ORDER } from "../models/bodyMetricTypes";

/** Oldest → newest; two readings of one day keep the order they were recorded in. */
const byMeasuredOn = (a: IBodyMetricEntry, b: IBodyMetricEntry) =>
  a.measuredOn.localeCompare(b.measuredOn) || a.createdOn.localeCompare(b.createdOn);

/** One summary per metric that has readings, in the picker order. */
export const summarizeBodyMetrics = (entries: IBodyMetricEntry[]): IBodyMetricSummary[] =>
  BODY_METRIC_TYPE_ORDER.flatMap((type) => {
    const history = entries.filter((e) => e.type === type).sort(byMeasuredOn);
    if (!history.length) return [];
    const latest = history[history.length - 1];
    const previous = history.length > 1 ? history[history.length - 2] : null;
    return [{ type, latest, change: previous ? latest.value - previous.value : null, history }];
  });

const rangeStart = (range: BodyMetricRange, now: Date): Date | null => {
  switch (range) {
    case "1m":
      return subMonths(now, 1);
    case "3m":
      return subMonths(now, 3);
    case "6m":
      return subMonths(now, 6);
    case "1y":
      return subYears(now, 1);
    default:
      return null;
  }
};

/** The readings (oldest → newest) measured inside the range ending now. */
export const filterByRange = (
  history: IBodyMetricEntry[],
  range: BodyMetricRange,
  now: Date = new Date(),
): IBodyMetricEntry[] => {
  const start = rangeStart(range, now);
  return start ? history.filter((e) => new Date(e.measuredOn) >= start) : history;
};

/** Every reading with its change from the reading before it (null for the first). */
export const withChanges = (history: IBodyMetricEntry[]) =>
  history.map((entry, index) => ({
    entry,
    change: index === 0 ? null : roundTo(entry.value - history[index - 1].value, 2),
  }));

const roundTo = (value: number, decimals: number) => {
  const factor = 10 ** decimals;
  return Math.round(value * factor) / factor;
};

/** "82,4" - the number only, with the metric's decimals. */
export const formatBodyMetricNumber = (type: BodyMetricType, value: number): string =>
  roundTo(value, BODY_METRIC_TYPES[type].decimals).toLocaleString("bg-BG", {
    maximumFractionDigits: BODY_METRIC_TYPES[type].decimals,
  });

/** "82,4 кг". */
export const formatBodyMetricValue = (type: BodyMetricType, value: number): string =>
  `${formatBodyMetricNumber(type, value)} ${BODY_METRIC_TYPES[type].unit}`;

/** "+1,4 кг", "−2 см", "0 см". */
export const formatBodyMetricChange = (type: BodyMetricType, change: number): string => {
  const rounded = roundTo(change, BODY_METRIC_TYPES[type].decimals);
  const sign = rounded > 0 ? "+" : rounded < 0 ? "−" : "";
  return `${sign}${formatBodyMetricValue(type, Math.abs(rounded))}`;
};

/** Local day → `YYYY-MM-DD` (the wall-clock day the user picked, no timezone shift). */
export const toApiDate = (date: Date): string => {
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}`;
};
